
import React, { useState, useEffect } from "react";
import { Apple, Smartphone, X, Check } from "lucide-react";
import { 
  Dialog, 
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

// Local storage key for tracking dialog visibility
const INSTALL_PROMPT_KEY = "admin-install-app-prompt-dismissed";

interface InstallAppDialogProps {
  isAdmin: boolean;
}

const InstallAppDialog = ({ isAdmin }: InstallAppDialogProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android" | null>(null);
  
  // Detect platform on client side only
  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        setPlatform("ios");
      } else if (/Android/.test(navigator.userAgent)) {
        setPlatform("android");
      }
    }
  }, []);

  // Only show the dialog if user is admin on mobile and hasn't dismissed it
  useEffect(() => {
    if (isAdmin && isMobile && platform) {
      const isDismissed = localStorage.getItem(INSTALL_PROMPT_KEY) === "true";
      if (!isDismissed) {
        // Delay showing the prompt slightly for better UX
        const timer = setTimeout(() => setOpen(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isAdmin, isMobile, platform]);

  // Handle permanent dismissal
  const handleDismissPermanently = () => {
    localStorage.setItem(INSTALL_PROMPT_KEY, "true");
    setOpen(false);
  };
  
  // Handle temporary dismissal (will show again next session)
  const handleDismiss = () => {
    setOpen(false);
  };

  // Don't render anything if conditions aren't met
  if (!isAdmin || !isMobile || !platform) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {platform === "ios" ? (
              <Apple className="h-5 w-5" />
            ) : (
              <Smartphone className="h-5 w-5" />
            )}
            Install LabSwAPP on your home screen
          </DialogTitle>
        </DialogHeader>
        
        {platform === "ios" ? (
          <div className="space-y-4">
            <p className="text-sm">For easier access, add LabSwAPP to your iPhone home screen:</p>
            
            <ol className="list-decimal pl-5 space-y-3 text-sm">
              <li>Tap the <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-xs">Share <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span> icon at the bottom of your browser</li>
              <li>Scroll down and tap <span className="font-medium">Add to Home Screen</span></li>
              <li>Tap <span className="font-medium">Add</span> in the top right corner</li>
            </ol>
            
            <div className="bg-blue-50 p-3 rounded-md text-xs text-blue-800">
              You'll be able to access the admin dashboard directly from your home screen with a single tap.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm">For easier access, add LabSwAPP to your Android home screen:</p>
            
            <ol className="list-decimal pl-5 space-y-3 text-sm">
              <li>Tap the menu icon (three dots) in the top-right of your browser</li>
              <li>Select <span className="font-medium">Install app</span> or <span className="font-medium">Add to Home screen</span></li>
              <li>Follow the on-screen instructions to complete installation</li>
            </ol>
            
            <div className="bg-green-50 p-3 rounded-md text-xs text-green-800">
              You'll be able to access the admin dashboard directly from your home screen with a single tap.
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
          <Button 
            variant="outline" 
            className="flex items-center gap-1" 
            onClick={handleDismissPermanently}
          >
            <X className="h-4 w-4" />
            Don't show again
          </Button>
          <Button 
            className="flex items-center gap-1" 
            onClick={handleDismiss}
          >
            <Check className="h-4 w-4" />
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InstallAppDialog;
